import {UIGameChatContainer} from '../UIGameChatContainer/UIGameChatContainer'
import React, {memo, ReactElement, useEffect, useMemo, useState} from 'react'
import styles from './UIGameChat.module.scss'
import feedStyles from '../UIGameChatContainer/UIGameChatContainer.module.scss'
import {useUIGameStateContext} from '../../../contexts/UIGameStateContext'
import {UIGameFeedMessage} from '../UIGameFeedMessage/UIGameFeedMessage'
import {GameValidator} from '@colonist/game-mechanic-validators'
import {GameChatMessageData, NestedReadonly, GameChatType} from '@colonist/game-states'
import {createFeedMessageDataForGameChatData} from '../UIGameFeedMessage/FeedMessageDataChats'
import {useUIGameUserStatesContext} from '../../../contexts/UIGameUserStatesContext'
import {UIGameFeedScrollContainer} from '../UIGameFeedScrollContainer/UIGameFeedScrollContainer'
import {UIGameChatDebugMessage} from './UIGameChatDebugMessage'
import {t} from '../../../common/T'
import {useUIMutePlayerContext} from '../../../contexts/UIMutePlayerContext'
// eslint-disable-next-line no-restricted-imports
import {Sound} from '../../../../../../code/client/shared/scripts/Sound'
// eslint-disable-next-line no-restricted-imports
import {getLobbyImagePath} from '../../../../../../code/client/shared/scripts/ui_utils/Utils'
import {LobbyImage} from '@colonist/types'
import {cloneDeep} from 'lodash'
// eslint-disable-next-line no-restricted-imports
import {ClientUserState} from '../../../../../../code/client/shared/scripts/ClientUserState'
import {useUIGameSettingsContext} from '../../../contexts/UIGameSettingsContext'
import {useUIGamePlayersContext} from '../../../contexts/UIGamePlayersContext'
import {useUIGameMyColorContext} from '../../../contexts/UIGameMyColorContext'

const UIGameChatFeedMessage = memo(function UIGameChatFeedMessage({
    gameValidator,
    gameChatData,
}: {
    gameValidator: GameValidator
    gameChatData: NestedReadonly<GameChatMessageData>
}) {
    const feedMessageData = createFeedMessageDataForGameChatData(gameValidator, gameChatData)

    return (
        <UIGameFeedMessage
            message={feedMessageData}
        />
    )
})

const FIRST_MESSAGE_LOCAL_KEY = 'local0'
const monitoringChatData: GameChatMessageData = {
    type: GameChatType.SystemMessage,
    message: `Chat is being monitored. Please be respectful.`,
}

// TODO: @Dang remove this when SocketWebActionController fully migrated to React.
let globalAddSpectatorMessage: undefined | ((data: GameChatMessageData) => void)
export function addGlobalSpectatorMessage(data: GameChatMessageData) {
    if(globalAddSpectatorMessage) globalAddSpectatorMessage(data)
}

export function UIGameChat({
    isChatVisible,
    onSendChat,
    isDefaultMinimized = false,
}: {
    isChatVisible: boolean
    onSendChat: (message: string) => void
    isDefaultMinimized?: boolean | undefined
}) {
    const gameState = useUIGameStateContext()
    const {getPlayerValidator} = useUIGamePlayersContext()
    const {isPrivateGame} = useUIGameSettingsContext()
    const {isMyColorSpectator} = useUIGameMyColorContext()

    const [isMinimized, setIsMinimized] = useState(isDefaultMinimized)
    const [wasChatMonitoringWarningShown, setWasChatMonitoringWarningShown] = useState(false)

    const [inputValue, setInputValue] = useState('')
    const [localMessages, setLocalMessages] = useState<{[key: string]: ReactElement[]}>({})
    const {mutedPlayers} = useUIMutePlayerContext()
    const {userStates, hasOnlyOneHumanPlayer} = useUIGameUserStatesContext()
    const inCohortToSeeChatIsBeingMonitoredMessage = ClientUserState.abTests.CHAT_TOXICITY_SHOW_MONITORED_WARNING == 'SHOW_CHAT_IS_MONITORED_WARNING'

    globalAddSpectatorMessage = (data: GameChatMessageData) => {
        const messageElement = <UIGameChatFeedMessage
            gameChatData={data}
            gameValidator={gameValidator}
        />
        sendLocalMessage(messageElement)
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const message = inputValue.trim()
        if(message === '') return

        setInputValue('')

        if(message === '/debug') sendLocalMessage(<UIGameChatDebugMessage />)
        else onSendChat(message)
    }

    const sendLocalMessage = (message: ReactElement) => {
        const key = Object.keys(gameState.gameChatState).at(-1) ?? FIRST_MESSAGE_LOCAL_KEY
        const localMessagesAtKey = localMessages[key] ?? []
        const messageWithKey = <div key={`${key}-${localMessagesAtKey.length}`}>
            {message}
        </div>

        setLocalMessages({
            ...localMessages,
            [key]: [...localMessagesAtKey, messageWithKey],
        })
    }

    const gameValidator = useMemo(() => {
        const clonedGameState = cloneDeep(gameState)
        for(const playerColorKey in clonedGameState.playerStates) {
            clonedGameState.playerStates[playerColorKey].isConnected = true
        }

        return new GameValidator(gameState, userStates)
    }, []) // states excluded to avoid re-renders in memoized components and the states don't affect the user info

    useEffect(() => {
        if(isMyColorSpectator) return
        if(isMinimized) return
        if(isPrivateGame) return
        if(wasChatMonitoringWarningShown) return
        if(hasOnlyOneHumanPlayer === true) return
        if(!inCohortToSeeChatIsBeingMonitoredMessage) return

        setWasChatMonitoringWarningShown(true)

        sendLocalMessage(<UIGameChatFeedMessage
            gameChatData={monitoringChatData}
            gameValidator={gameValidator}
        />)
    }, [isMinimized, wasChatMonitoringWarningShown])

    const messages = useMemo(() => {
        const msgs: ReactElement[] = []
        if(localMessages[FIRST_MESSAGE_LOCAL_KEY] !== undefined) msgs.push(...localMessages[FIRST_MESSAGE_LOCAL_KEY])
        for(const key in gameState.gameChatState) {
            const gameChatData = gameState.gameChatState[key].text
            if(gameChatData.type === GameChatType.Message && gameChatData.from != null) {
                const username = getPlayerValidator(gameChatData.from)?.username
                if(username != null && mutedPlayers.includes(username)) continue
            }

            msgs.push(<UIGameChatFeedMessage
                gameChatData={gameChatData}
                gameValidator={gameValidator}
                key={key}
            />)

            const localMessagesAtKey = localMessages[key]
            if(localMessagesAtKey !== undefined) msgs.push(...localMessagesAtKey)
        }
        return msgs
    }, [gameState, localMessages, mutedPlayers])

    const numberOfMessages = Object.keys(messages).length
    const prevMessagesCountRef = React.useRef(numberOfMessages)
    const hasNewMessages = numberOfMessages > prevMessagesCountRef.current

    useEffect(() => {
        if(hasNewMessages) {
            Sound.playSound(Sound.SoundType.MessageNotification)
        }
        prevMessagesCountRef.current = numberOfMessages
    }, [hasNewMessages, numberOfMessages, mutedPlayers])

    const [numberOfReadMessages, setNumberOfReadMessages] = useState(numberOfMessages)

    const numberOfUnreadMessages = useMemo(() => numberOfMessages - numberOfReadMessages, [numberOfReadMessages, numberOfMessages])

    useEffect(() => {
        if(isMinimized) return

        setNumberOfReadMessages(numberOfMessages)
    }, [isMinimized, numberOfMessages])

    const showMinimizedNotification = numberOfUnreadMessages > 0

    if(!isChatVisible) return null

    return (
        <UIGameChatContainer
            isMinimized={isMinimized}
            minimizedTitle={t('strings:imgAlts.Chat')}
            setIsMinimized={setIsMinimized}
            showMinimizedNotification={showMinimizedNotification}>
            <div className={styles.chatContainer}>
                <UIGameFeedScrollContainer>
                    {messages}
                </UIGameFeedScrollContainer>
                <div className={styles.chatBottomContainer}>
                    <form className={styles.formChat} onSubmit={handleSubmit}>
                        <input
                            className={styles.inputText}
                            data-testid='game-chat-input'
                            maxLength={200}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={t('strings:homePage.header.sendMessage')}
                            type="text"
                            value={inputValue} />
                        <input
                            className={styles.inputSubmit}
                            data-testid='game-chat-submit'
                            src={getLobbyImagePath(LobbyImage.SendArrow)}
                            type="image" />
                    </form>
                    <div
                        className={feedStyles.chatMinimizeButton}
                        onClick={() => setIsMinimized(true)}
                    >
                        <img src={getLobbyImagePath(LobbyImage.DownArrowBlack)} />
                    </div>
                </div>
            </div>
        </UIGameChatContainer>
    )
}
